"use client";
// ===================================================
// מחברת — the tray: tabs of notes the user chose to keep open.
// Order is the user's (drag with mouse, touch or keyboard); activity never
// reorders it. Closing a tab never deletes or archives the note.
// Spec: docs/specs/pinkas-unified-workspace-20260924.md §4
// ===================================================
import { useEffect, useRef } from "react";
import { X } from "lucide-react";
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  horizontalListSortingStrategy,
  sortableKeyboardCoordinates,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Button } from "@/components/ui";
import type { PinkasItem } from "@/lib/cc-pinkas-queries";
import { noteColorHex } from "@/lib/pinkas/vocab";

export function PinkasTray({
  tray,
  itemsById,
  activeId,
  onSelect,
  onClose,
  onMove,
}: {
  tray: string[];
  itemsById: Map<string, PinkasItem>;
  activeId: string | null;
  onSelect: (id: string) => void;
  onClose: (id: string) => void;
  onMove: (fromId: string, toId: string) => void;
}) {
  const sensors = useSensors(
    // A small distance keeps a plain click a click.
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 220, tolerance: 6 } }),
    // Enter stays "open this tab"; Space picks the tab up, arrows move it, Space drops.
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
      keyboardCodes: { start: ["Space"], cancel: ["Escape"], end: ["Space", "Enter"] },
    }),
  );
  const scroller = useRef<HTMLDivElement>(null);

  // Keep the active tab reachable when the tray overflows.
  useEffect(() => {
    if (!activeId || !scroller.current) return;
    const el = scroller.current.querySelector<HTMLElement>(`[data-tray-id="${globalThis.CSS?.escape ? globalThis.CSS.escape(activeId) : activeId}"]`);
    el?.scrollIntoView?.({ block: "nearest", inline: "nearest" });
  }, [activeId, tray]);

  function onDragEnd(e: DragEndEvent) {
    if (e.over && e.active.id !== e.over.id) onMove(String(e.active.id), String(e.over.id));
  }

  const visible = tray.filter((id) => itemsById.has(id));
  if (visible.length === 0) {
    return (
      <div className="px-3 py-1.5 text-[11px] text-[var(--theme-text-faint)]">
        המגש ריק — ״השאר במגש״ מהרשימה או מהפתק שומר כאן לשונית
      </div>
    );
  }

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
      <SortableContext items={visible} strategy={horizontalListSortingStrategy}>
        <div
          ref={scroller}
          role="tablist"
          aria-label="פתקים פתוחים במגש"
          className="flex min-w-0 items-center gap-1 overflow-x-auto px-2 py-1 [scrollbar-width:thin]"
        >
          {visible.map((id) => (
            <TrayTab
              key={id}
              item={itemsById.get(id)!}
              active={id === activeId}
              onSelect={() => onSelect(id)}
              onClose={() => onClose(id)}
            />
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
}

function TrayTab({
  item,
  active,
  onSelect,
  onClose,
}: {
  item: PinkasItem;
  active: boolean;
  onSelect: () => void;
  onClose: () => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: item.id });
  return (
    <div
      ref={setNodeRef}
      data-tray-id={item.id}
      style={{ transform: CSS.Translate.toString(transform), transition, zIndex: isDragging ? 1 : undefined }}
      className={`group flex max-w-[190px] shrink-0 items-center rounded-md border ${
        active
          ? "border-[var(--role-selection-border)] bg-[var(--role-selection-bg)]"
          : "border-transparent hover:bg-[var(--role-hover-bg)]"
      } ${isDragging ? "opacity-80 shadow-lg" : ""}`}
    >
      <Button
        {...attributes}
        {...listeners}
        type="button"
        variant="ghost"
        size="sm"
        role="tab"
        aria-roledescription="לשונית ניתנת לגרירה"
        aria-selected={active}
        title={`${item.text} — גרור, או רווח ואז חיצים, לשינוי סדר`}
        onClick={onSelect}
        onKeyDown={(e) => {
          // Delete closes the focused tab, like a browser tab strip.
          if (e.key === "Delete") {
            e.preventDefault();
            onClose();
            return;
          }
          listeners?.onKeyDown?.(e);
        }}
        className={`min-w-0 justify-start ${active ? "text-[var(--role-selection-fg)] font-semibold" : "text-[var(--theme-text-muted)]"}`}
        style={{ height: 26, paddingInline: 8, borderRadius: 6, background: "transparent" }}
      >
        {item.color ? (
          <span aria-hidden className="h-2 w-2 shrink-0 rounded-full" style={{ background: noteColorHex(item.color) }} />
        ) : null}
        <span className="truncate">{item.text}</span>
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        icon={X}
        aria-label={`סגור לשונית: ${item.text}`}
        title="סגור לשונית (הפתק נשאר)"
        onClick={onClose}
        className="text-[var(--theme-text-faint)] opacity-70 group-hover:opacity-100 focus-visible:opacity-100"
        style={{ width: 22, height: 22, padding: 0, borderRadius: 5 }}
      />
    </div>
  );
}
